import { observer } from "mobx-react-lite";
import { usePostHog } from "posthog-js/react";
import { Marker, useMap } from "react-map-gl/maplibre";
import { useDragging } from "../contexts/DraggingContext";
import type AircraftModel from "../model/AircraftModel";
import { setCurrentAircraftId } from "../model/CurrentAircraft";
import {
	cwpStore,
	mapViewportStore,
	roleConfigurationStore,
	trajectoryPredictionStore,
} from "../state";
// import AircraftPopup from "./AircraftPopup";
import DraggableMarker from "./DraggableMarker";

// Memoized function to calculate hexagon points - cached for performance
// Using numeric keys with bit shifting for optimal performance
const hexPointsCache = new Map<number, string>();
const getRegularHexPoints = (
	size: number,
	inset = 1,
	strokeWidth = 2,
): string => {
	// Create a numeric key using bit shifting
	// size: 0-255 (8 bits), inset: 0-15 (4 bits), strokeWidth*10: 0-63 (6 bits)
	const cacheKey =
		(size << 10) | (inset << 6) | (Math.round(strokeWidth * 10) & 0x3f);
	const cached = hexPointsCache.get(cacheKey);
	if (cached) {
		return cached;
	}

	const cx = size / 2;
	const cy = size / 2;
	const margin = inset + strokeWidth / 2;
	const r = Math.max(0, size / 2 - margin);
	const anglesDeg = [-90, -30, 30, 90, 150, 210]; // pointy-top hex
	const points = anglesDeg
		.map((deg) => {
			const rad = (deg * Math.PI) / 180;
			const x = cx + r * Math.cos(rad);
			const y = cy + r * Math.sin(rad);
			return `${x.toFixed(2)},${y.toFixed(2)}`;
		})
		.join(" ");

	hexPointsCache.set(cacheKey, points);
	return points;
};

/**
 * AircraftMarker component displays a marker for an aircraft on the map.
 * It shows the aircraft's last known position and a trail of its previous positions.
 * Clicking on the marker toggles the selection of the aircraft in the CWP store.
 */
export default observer(function AircraftMarker(properties: {
	aircraft: AircraftModel;
}) {
	const posthog = usePostHog();
	const {
		lastKnownLongitude: lon,
		lastKnownLatitude: lat,
		lastKnownBearing: bearing,
		// lastKnownSpeed: speed,
		aircraftId,
		positionHistory,
	} = properties.aircraft;
	const {
		hoveredMarkerAircraftId,
		hoveredConflictAircraftIds,
		selectedAircraftIds,
	} = cwpStore;
	const { isStillDragging } = useDragging();
	const isHovered =
		hoveredMarkerAircraftId === aircraftId ||
		hoveredConflictAircraftIds.has(aircraftId);
	const history = positionHistory.slice(0, 8);
	const isSelected = selectedAircraftIds.has(aircraftId);

	const onClickOnAircraft = (): void => {
		const wasSelected = selectedAircraftIds.has(aircraftId);
		cwpStore.toggleFlightRouteForAircraft(aircraftId);
		cwpStore.toggleSelectedAircraftId(aircraftId);
		setCurrentAircraftId(aircraftId);

		posthog?.capture("aircraft_clicked", {
			aircraft_id: aircraftId,
			callsign: properties.aircraft.callSign,
			action: wasSelected ? "deselect" : "select",
			altitude: properties.aircraft.lastKnownAltitude,
			speed: properties.aircraft.lastKnownSpeed,
			bearing: bearing,
			position: { lat, lon },
			selected_count: selectedAircraftIds.size,
		});
	};

	const onHoverOnAircraft = (): void => {
		if (isStillDragging() || mapViewportStore.isMoving) {
			return;
		}
		cwpStore.setFlightRouteForAircraft(aircraftId, true);
		cwpStore.setHoveredMarkerAircraftId(aircraftId);

		posthog?.capture("aircraft_hover_start", {
			aircraft_id: aircraftId,
			callsign: properties.aircraft.callSign,
			altitude: properties.aircraft.lastKnownAltitude,
			speed: properties.aircraft.lastKnownSpeed,
			is_selected: isSelected,
		});
	};
	const onHoverOffAircraft = (): void => {
		if (!cwpStore.selectedAircraftIds.has(aircraftId)) {
			cwpStore.setFlightRouteForAircraft(aircraftId, false);
		}
		cwpStore.removeHoveredMarkerAircraftId();

		posthog?.capture("aircraft_hover_end", {
			aircraft_id: aircraftId,
			callsign: properties.aircraft.callSign,
		});
	};

	const { current: map } = useMap();

	// Utility function to calculate mouse position from event
	const calculateMousePosition = (
		event: React.MouseEvent | MouseEvent,
	): { lat: number; lng: number } | null => {
		if (!map) {
			return null;
		}

		const canvas = map.getCanvas();
		const rect = canvas.getBoundingClientRect();
		const x = event.clientX - rect.left;
		const y = event.clientY - rect.top;
		const lngLat = map.unproject([x, y]);

		return { lat: lngLat.lat, lng: lngLat.lng };
	};

	const onDragStartAircraft = (
		_offsetX: number,
		_offsetY: number,
		lng: number,
		lat: number,
		event?: React.MouseEvent | MouseEvent,
	): void => {
		// Enable trajectory prediction and set this as the main aircraft
		trajectoryPredictionStore.setEnabled(true);
		trajectoryPredictionStore.setMainAircraft(aircraftId);
		trajectoryPredictionStore.setDraggedHandlePosition(lat, lng);

		// Set mouse position if event is available
		if (event) {
			const mousePos = calculateMousePosition(event);
			if (mousePos) {
				trajectoryPredictionStore.setMousePosition(mousePos.lat, mousePos.lng);
			}
		}

		posthog?.capture("aircraft_drag_start", {
			aircraft_id: aircraftId,
			callsign: properties.aircraft.callSign,
			drag_start_position: { lat, lng },
			original_position: {
				lat: properties.aircraft.lastKnownLatitude,
				lon: properties.aircraft.lastKnownLongitude,
			},
			altitude: properties.aircraft.lastKnownAltitude,
		});
	};

	const onDragAircraft = (
		_offsetX: number,
		_offsetY: number,
		lng: number,
		lat: number,
		event?: React.MouseEvent | MouseEvent,
	): void => {
		// Update the dragged handle position
		trajectoryPredictionStore.setDraggedHandlePosition(lat, lng);

		// Update mouse position if event is available
		if (event) {
			const mousePos = calculateMousePosition(event);
			if (mousePos) {
				trajectoryPredictionStore.setMousePosition(mousePos.lat, mousePos.lng);
			}
		}
	};

	const onDragStopAircraft = (
		_offsetX: number,
		_offsetY: number,
		lng: number,
		lat: number,
	): void => {
		// Disable trajectory prediction when drag ends
		trajectoryPredictionStore.setEnabled(false);

		posthog?.capture("aircraft_drag_end", {
			aircraft_id: aircraftId,
			callsign: properties.aircraft.callSign,
			drag_end_position: { lat, lng },
			original_position: {
				lat: properties.aircraft.lastKnownLatitude,
				lon: properties.aircraft.lastKnownLongitude,
			},
			drag_distance: Math.hypot(
				lng - properties.aircraft.lastKnownLongitude,
				lat - properties.aircraft.lastKnownLatitude,
			),
		});
	};

	return (
		<>
			{history.map((pos, index) => {
				const size = 8 + (6 - index) * 2;
				const opacity = 1;
				const radius = (size - 5) / 2 - 1.5;
				if (radius <= 0.5) {
					return null;
				}
				return (
					<Marker
						key={`trail-${index}`}
						longitude={pos.lon}
						latitude={pos.lat}
						className="cursor-pointer"
						onClick={onClickOnAircraft}
					>
						<svg
							width={size}
							height={size}
							viewBox={`0 0 ${size} ${size}`}
							style={{
								transform: `translate(-50%, -50%)`, // ensures SVG is centered on marker
								position: "absolute",
								left: "50%",
								top: "50%",
								opacity,
							}}
							onMouseEnter={onHoverOnAircraft}
							onMouseLeave={onHoverOffAircraft}
						>
							{isSelected && index === 0 ? (
								<polygon
									points={getRegularHexPoints(size, 1, 1.5)}
									transform={`rotate(${bearing} ${size / 2} ${size / 2})`}
									fill={isHovered ? "#00ffff" : "transparent"}
									stroke={"#00ffff"}
									strokeWidth="1.5"
								></polygon>
							) : null}
							<circle
								cx="50%"
								cy="50%"
								r={radius}
								fill={isHovered ? "#00ffff" : "transparent"}
								stroke={
									isHovered
										? "#00ffff"
										: roleConfigurationStore.getOriginalColorOfAircraft(
												aircraftId,
											)
								}
								strokeWidth="1"
							/>
						</svg>
					</Marker>
				);
			})}

			{isSelected ? (
				<DraggableMarker
					longitude={lon}
					latitude={lat}
					onMouseEnter={onHoverOnAircraft}
					onMouseLeave={onHoverOffAircraft}
					onClick={onClickOnAircraft}
					onDragStart={onDragStartAircraft}
					onDrag={onDragAircraft}
					onDragStop={onDragStopAircraft}
				>
					<div className="w-6 h-6" />
				</DraggableMarker>
			) : null}
			{/* <Marker longitude={lon} latitude={lat}>
				<AircraftPopup aircraft={properties.aircraft} pseudo={pseudo} />
			</Marker> */}
		</>
	);
});
